<template>
  <el-dialog
    :title="modalTitle"
    :visible.sync="formModalOpened"
    :before-close="handleClose"
  >
    <el-row :gutter="20">
      <el-col :span="20" :offset="2">
        <el-form
          ref="createUserForm"
          :model="formData"
          :rules="rules"
          :label-width="$root.labelWidth"
          label-position="top"
          v-loading="creating">
          <el-row :gutter="20">
            <el-col :span="8">
              <el-form-item :label="$t('modules.user.fields.email.label')" prop="email">
                <el-input type="email" v-model.lazy.trim="formData.email" :placeholder="$t('modules.user.fields.email.placeholder')" :disabled="editMode"></el-input>
              </el-form-item>
            </el-col>
            <el-col :span="8">
              <el-form-item :label="$t('modules.user.fields.locale.label')" prop="locale">
                <el-select filterable
                  remote
                  reserve-keyword
                  :placeholder="$t('modules.user.fields.locale.placeholder')" class="full-width" v-model.lazy="formData.locale">
                  <el-option v-for="(localeConfig, locale) in locales" :key="locale" :value="locale" :label="localeConfig.name"></el-option>
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="8">
              <el-form-item :label="$t('modules.user.fields.timezone.label')" prop="timezone">
                <el-select filterable
                  remote
                  reserve-keyword
                  :placeholder="$t('modules.user.fields.timezone.placeholder')" class="full-width" v-model.lazy="formData.timezone">
                  <el-option v-for="(timezone, index) in timezones" :key="index" :value="timezone" :label="timezone"></el-option>
                </el-select>
              </el-form-item>
            </el-col>
          </el-row>
          <el-row :gutter="20">
            <el-col :span="12">
              <el-form-item :label="$t('modules.user.fields.password.label')" prop="password">
                <el-input type="password" v-model.lazy.trim="formData.password" :placeholder="$t('modules.user.fields.password.placeholder')"></el-input>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item :label="$t('modules.user.fields.passwordConfirmation.label')" prop="passwordConfirmation">
                <el-input type="password" v-model.lazy.trim="formData.password_confirmation" :placeholder="$t('modules.user.fields.passwordConfirmation.placeholder')"></el-input>
              </el-form-item>
            </el-col>
          </el-row>
          <el-row :gutter="20">
            <el-col :span="12">
              <el-form-item :label="$t('modules.user.fields.employmentType.label')" prop="employmentType">
              <el-select v-model="formData.employment_type" :placeholder="$t('modules.user.fields.employmentType.placeholder')" @change="setEmploymentType">
                <el-option value="full_time">Full Time</el-option>
                <el-option value="half_time">Half Time</el-option>
              </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item prop="joining_date" label="Joining Date">
                <el-col :span="12">
                  <el-date-picker
                    size="large"
                    class="full-width"
                    v-model="formData.joining_date"
                    type="date"
                    align="right"
                    placeholder="Joining Date"
                    :value-format="'yyyy-MM-dd'"
                    format="yyyy-MM-dd">
                  </el-date-picker>
                </el-col>
              </el-form-item>
            </el-col>  
          </el-row>
          <el-row :gutter="20">
            <el-col :span="12">
              <el-form-item :label="$t('modules.user.fields.remainingPtoDays.label')" prop="remainingPtoDays">
                <el-input type="number" v-model.lazy.trim="formData.remaining_pto_days" :placeholder="$t('modules.user.fields.remainingPtoDays.placeholder')"></el-input>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item :label="$t('modules.user.fields.remainingSicknessDays.label')" prop="remainingSicknessDays">
                <el-input type="number" v-model.lazy.trim="formData.remaining_sickness_days" :placeholder="$t('modules.user.fields.remainingSicknessDays.placeholder')"></el-input>
              </el-form-item>
            </el-col>
          </el-row>
          <el-row :gutter="20">
            <el-col :span="12">
              <el-form-item :label="$t('modules.user.fields.admin.label')" prop="admin">
                <el-switch
                  v-model="formData.admin">
                </el-switch>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item :label="$t('modules.user.fields.master.label')" prop="master">
                <el-switch
                  v-model="formData.master">
                </el-switch>
              </el-form-item>
            </el-col>
          </el-row>
        </el-form>
      </el-col>
    </el-row>
    <span slot="footer" class="dialog-footer">
      <el-button type="primary" @click="submitForm()" :loading="processing">Save</el-button>
    </span>
  </el-dialog>
</template>

<script>
  import { mapActions, mapGetters } from 'vuex';
  import { get } from 'lodash';
  import moment from 'moment';
  import 'moment-timezone';

  import validations from '@/utils/validations';
  import formPopulator from '@/mixins/form_populator';
  import { showSuccessMessage, showErrorMessage } from '@/ui/message';
  import availableLocales from '@/i18n/available_locales';

  export default {
    name: 'UserFormModal',
    mixins: [formPopulator],
    data() {
      return {
        finishedStep: 0,
        processing: false,
        locales: availableLocales,
        timezones: moment.tz.names(),
        rules: {
          email: [
            { ...validations.email },
            { ...validations.required },
          ],
          locale: [
            { ...validations.required },
          ],
          timezone: [
            { ...validations.required },
          ],
        },
        formData: {
          admin: false,
          master: false,
          email: '',
          password: '',
          password_confirmation: '',
          locale: '',
          employment_type: 'Full Time',
          remaining_pto_days: '',
          remaining_sickness_days: '',
          joining_date: '',
          timezone: '',
        },
      };
    },
    computed: {
      ...mapGetters('entities/users', ['creating', 'formModalContext']),

      formModalOpened: {
        get() {
          return this.$store.getters['entities/users/formModalOpened'];
        },
        set(newValue) {
          if (!newValue) {
            this.handleClose();
          }
        },
      },

      editMode() {
        return get(this, 'formModalContext.userId') !== undefined;
      },

      modalTitle() {
        return this.editMode ? 'Edit User' : 'Add New User';
      },
    },
    methods: {
      ...mapActions('entities/users', ['createUser', 'updateUser', 'closeFormModal']),

      setEmploymentType(value) {
        this.employment_type = value;
      },
      // handleClickSubmit() {
      //   this.$refs.form.submitForm();
      // },

      submitForm() {
        this.$refs.createUserForm.validate((valid) => {
          if (valid) {
            if (this.editMode) {
              this.updateUser({ id: this.formModalContext.userId, data: this.formData })
                .then((response) => {
                  if (response.status) {
                    showSuccessMessage('User account has been edited successfully');
                    this.handleClose();
                  } else {
                    showErrorMessage(response.error);
                  }
                });
            } else {
              this.createUser(this.formData)
                .then((response) => {
                  if (response.status) {
                    showSuccessMessage('User account has been created.');
                    this.handleClose();
                  } else {
                    showErrorMessage(response.error);
                  }
                });
            }
          } else {
            showErrorMessage(this.$t('form.messages.notValid'));
            return false;
          }
          return true;
        });
      },

      populateForm() {
        const user = this.$store.getters['entities/users/find'](this.formModalContext.userId);
        const data = { ...user };
        this.populateFormWithModel(data);
      },

      clearForm() {
        this.$refs.createUserForm.resetFields();
      },

      handleClose() {
        this.closeFormModal();
        this.clearForm();
      },
    },
    watch: {
      editMode(newValue) {
        if (newValue) {
          this.populateForm();
        }
      },
    },
  };
</script>



// WEBPACK FOOTER //
// src/components/user/UserFormModal.vue